/* Cocoon — reading preferences.
 *
 * A preferences control, NOT an accessibility overlay. The distinction matters:
 * overlays reach into the host page and rewrite its roles and labels, and that
 * is what makes them harmful. This script touches three things and nothing
 * else — its own panel, its own launcher, and two data attributes on <html>.
 *
 * It never sets a role, aria-label, alt or tabindex on any element outside its
 * own panel, and it never intercepts Tab. A keyboard user can always leave.
 *
 * Nothing here claims WCAG or ADA conformance.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var widget = document.querySelector('[data-a11y]');
  if (!widget) return;

  var toggle = widget.querySelector('[data-a11y-toggle]');
  var panel = widget.querySelector('[data-a11y-panel]');
  if (!toggle || !panel) return;

  // Known values. A stored preference is only ever COMPARED against these and
  // then used to set a data attribute — it is never written into the DOM, into
  // a style, or into innerHTML, so a crafted localStorage value has nowhere to
  // go. Anything unrecognised is discarded rather than passed through.
  var SIZES = ['default', 'large', 'larger'];
  var CONTRASTS = ['default', 'high'];
  var STORE_KEY = 'cocoon:reading-prefs';

  /* ---- storage (may be unavailable in private modes) -------------------- */

  function load() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (!raw) return {};
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};                     // unreadable or unparseable: use defaults
    }
  }

  function save(prefs) {
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify(prefs));
    } catch (e) {
      /* Private browsing, or storage full. The preference still applies to
         this page; it just will not be remembered. Not worth an error. */
    }
  }

  /* ---- applying preferences --------------------------------------------- */

  function apply(prefs) {
    if (SIZES.indexOf(prefs.size) > 0) root.setAttribute('data-a11y-size', prefs.size);
    else root.removeAttribute('data-a11y-size');

    if (CONTRASTS.indexOf(prefs.contrast) > 0) root.setAttribute('data-a11y-contrast', prefs.contrast);
    else root.removeAttribute('data-a11y-contrast');

    reflect('data-a11y-size', prefs.size || 'default');
    reflect('data-a11y-contrast', prefs.contrast || 'default');
  }

  /** Keep aria-pressed honest on the widget's OWN buttons. */
  function reflect(attr, value) {
    var buttons = panel.querySelectorAll('[' + attr + ']');
    for (var i = 0; i < buttons.length; i += 1) {
      buttons[i].setAttribute('aria-pressed', String(buttons[i].getAttribute(attr) === value));
    }
  }

  var prefs = load();
  apply(prefs);

  function set(key, value, allowed) {
    if (allowed.indexOf(value) === -1) return;
    prefs[key] = value;
    apply(prefs);
    save(prefs);
  }

  panel.addEventListener('click', function (event) {
    var el = event.target.closest('button');
    if (!el) return;
    if (el.hasAttribute('data-a11y-size')) set('size', el.getAttribute('data-a11y-size'), SIZES);
    if (el.hasAttribute('data-a11y-contrast')) set('contrast', el.getAttribute('data-a11y-contrast'), CONTRASTS);
    if (el.hasAttribute('data-a11y-reset')) {
      prefs = {};
      apply(prefs);
      save(prefs);
      stopSpeaking();
    }
  });

  /* ---- open / close ------------------------------------------------------ */

  function open() {
    panel.hidden = false;
    toggle.setAttribute('aria-expanded', 'true');
  }

  function close(returnFocus) {
    panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    if (returnFocus) toggle.focus();
  }

  toggle.addEventListener('click', function () {
    if (panel.hidden) open(); else close(false);
  });

  // Escape closes and hands focus back. Tab is deliberately NOT intercepted:
  // trapping focus is the defect these widgets are notorious for.
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && !panel.hidden) close(true);
  });

  document.addEventListener('click', function (event) {
    if (!panel.hidden && !widget.contains(event.target)) close(false);
  });

  /* ---- read aloud -------------------------------------------------------- */

  var speechGroup = widget.querySelector('[data-a11y-speech]');
  var speakButton = widget.querySelector('[data-a11y-speak]');
  var speaking = false;
  var queue = [];
  var queueIndex = 0;

  // Browsers silently stop a long utterance after roughly fifteen seconds and
  // never fire `onend`. Speaking the whole page as ONE utterance therefore left
  // `speaking` true and the button stuck on "Stop reading" until a reload — you
  // could not even restart it. Short chunks each finish well inside that limit,
  // so the queue keeps moving and the last chunk's `onend` resets the button.
  var CHUNK_LIMIT = 200;

  /** Split at sentence boundaries, falling back to a hard cut for text that has
   *  no punctuation at all. */
  function chunk(text) {
    var out = [];
    var sentences = text.replace(/\s+/g, ' ').match(/[^.!?]+[.!?]*\s*/g) || [text];
    var buffer = '';
    for (var i = 0; i < sentences.length; i += 1) {
      var s = sentences[i];
      // A single sentence longer than the limit is cut on its own.
      while (s.length > CHUNK_LIMIT) {
        if (buffer) { out.push(buffer.trim()); buffer = ''; }
        out.push(s.slice(0, CHUNK_LIMIT).trim());
        s = s.slice(CHUNK_LIMIT);
      }
      if ((buffer + s).length > CHUNK_LIMIT) { out.push(buffer.trim()); buffer = s; }
      else { buffer += s; }
    }
    if (buffer.trim()) out.push(buffer.trim());
    return out.filter(Boolean);
  }

  function stopSpeaking() {
    if (!speaking) return;
    window.speechSynthesis.cancel();
    speaking = false;
    queue = [];
    queueIndex = 0;
    if (speakButton) {
      speakButton.setAttribute('aria-pressed', 'false');
      speakButton.textContent = 'Read this page';
    }
  }

  function speakNext() {
    if (!speaking || queueIndex >= queue.length) { stopSpeaking(); return; }
    var utterance = new window.SpeechSynthesisUtterance(queue[queueIndex]);
    queueIndex += 1;
    utterance.onend = speakNext;
    utterance.onerror = stopSpeaking;
    window.speechSynthesis.speak(utterance);
  }

  if (speechGroup && speakButton && 'speechSynthesis' in window) {
    // Only offered where the browser can actually do it. An inert control is
    // worse than an absent one.
    speechGroup.hidden = false;

    speakButton.addEventListener('click', function () {
      if (speaking) { stopSpeaking(); return; }

      // Main content only — reading the nav and footer on every page is noise.
      var main = document.getElementById('MainContent') || document.querySelector('main');
      var text = main ? (main.innerText || main.textContent || '').trim() : '';
      if (!text) return;

      queue = chunk(text);
      queueIndex = 0;
      if (!queue.length) return;

      window.speechSynthesis.cancel();
      speaking = true;
      speakButton.setAttribute('aria-pressed', 'true');
      speakButton.textContent = 'Stop reading';
      speakNext();
    });

    window.addEventListener('beforeunload', stopSpeaking);
  }

  // Reveal the launcher only now. Until this line runs the button is hidden by
  // CSS, so a blocked or failed script leaves no control that does nothing.
  root.classList.add('a11y-ready');
}());
